/**
 * Resizable shell geometry: the two column widths and the timeline height.
 *
 * The sizes live as CSS custom properties on the shell root (`--left-w`, `--right-w`, `--tl-h`)
 * rather than as inline widths on the columns, because the grid template is what actually places
 * them — one write moves the track, the splitter and the timeline together.
 *
 * Four rules shape everything below:
 *  - the viewport is the point of the app, so the columns are clamped against it (`viewportMin`)
 *    rather than against the window: two maxed-out columns must never squeeze the 3D view shut;
 *  - **DESIRED and APPLIED are two different numbers.** `desired` is what the user asked for
 *    (per-column limits only); `applied` is `desired` squeezed into the window that exists right
 *    now. Only `desired` is persisted, and only a user action changes it. Collapsing the two is a
 *    data-loss bug: shrink the window and a 560px column clamps to 428; widen it again and,
 *    without this split, the 560 is gone forever and the next drag writes 428 to disk;
 *  - a drag writes CSS on every pointer move but persists only through a 300 ms debounce, so one
 *    gesture is one IndexedDB write, not sixty;
 *  - the defaults are applied synchronously at install and the stored value arrives later, so the
 *    first painted frame is never unstyled and a failed read costs nothing.
 */

export interface LayoutSizes {
  left: number;
  right: number;
  timeline: number;
}

export const LAYOUT_DEFAULTS: LayoutSizes = { left: 280, right: 340, timeline: 240 };

export const LAYOUT_LIMITS = {
  left: { min: 200, max: 560 },
  right: { min: 200, max: 600 },
  /** `maxFraction` is of the shell's `clientHeight`. */
  timeline: { min: 120, maxFraction: 0.6 },
  /** The columns are clamped so the viewport track keeps at least this many px. */
  viewportMin: 320,
} as const;

export const LAYOUT_SETTINGS_KEY = 'ui.layout.v1';

/** The subset of `settingsRepo` this needs; tests pass a Map-backed stub. */
export interface LayoutStore {
  get<T>(key: string, fallback: T): Promise<T>;
  set<T>(key: string, value: T): Promise<void>;
}

export interface LayoutHandle {
  /** What is on screen right now: `desired()` clamped into the current window. */
  sizes(): LayoutSizes;
  /** What the user asked for. This is what gets persisted; a window resize never changes it. */
  desired(): LayoutSizes;
  /** Clamp, apply the CSS variables, and schedule the (debounced) persist. */
  set(partial: Partial<LayoutSizes>): void;
  /** Hide/show the timeline splitter; the shell owns the row itself (`.shell.no-timeline`). */
  setTimelineVisible(visible: boolean): void;
  /** True while a debounced write is queued or in flight (the persistence-sink contract). */
  pending(): boolean;
  /** Write any pending persist now and await it (and any write already in flight). */
  flush(): Promise<void>;
  dispose(): void;
}

export interface LayoutOptions {
  store?: LayoutStore;
  win?: Window;
}

/** Mirrors the `--gap` token in `src/style.css`; used only when the computed value is unreadable. */
const DEFAULT_GAP = 3;
/** One gesture, one write. */
const PERSIST_DEBOUNCE_MS = 300;

type SplitKey = 'left' | 'right' | 'timeline';

function clampNumber(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  return Math.min(max, Math.max(min, Math.round(value)));
}

/**
 * The shell's `--gap`, read from the live element so the arithmetic below cannot drift away from
 * the token. jsdom answers `''` for custom properties, hence the fallback.
 */
function readGap(shell: HTMLElement): number {
  if (typeof getComputedStyle !== 'function') return DEFAULT_GAP;
  const raw = getComputedStyle(shell).getPropertyValue('--gap').trim();
  const parsed = Number.parseFloat(raw);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : DEFAULT_GAP;
}

/**
 * Pointer capture, defensively.
 *
 * `releasePointerCapture` throws `NotFoundError` when the pointer is not (or no longer) captured,
 * and jsdom rejects the synthetic ids a test dispatches. Neither is a reason to abort a drag, and
 * losing capture only costs the drag its out-of-band pointer moves.
 */
function capture(node: Element, pointerId: number, on: boolean): void {
  try {
    if (on) node.setPointerCapture?.(pointerId);
    else node.releasePointerCapture?.(pointerId);
  } catch {
    /* not capturable here — the drag still works from the events we do get */
  }
}

/** Accept only what a previous version of this module could have written. */
function readStored(value: unknown): Partial<LayoutSizes> | null {
  if (typeof value !== 'object' || value === null) return null;
  const record = value as Record<string, unknown>;
  const out: Partial<LayoutSizes> = {};
  for (const key of ['left', 'right', 'timeline'] as const) {
    const candidate = record[key];
    if (typeof candidate === 'number' && Number.isFinite(candidate)) out[key] = candidate;
  }
  return Object.keys(out).length > 0 ? out : null;
}

export function installLayout(shell: HTMLElement, opts: LayoutOptions = {}): LayoutHandle {
  const store = opts.store ?? null;
  const win = opts.win ?? (typeof window === 'undefined' ? null : window);

  /** What the user asked for — persisted, and only ever changed by a user action. */
  const desired: LayoutSizes = { ...LAYOUT_DEFAULTS };
  /** What fits in the window right now — written to CSS, returned by `sizes()`. */
  const applied: LayoutSizes = { ...LAYOUT_DEFAULTS };
  let disposed = false;
  let persistTimer: ReturnType<typeof setTimeout> | null = null;
  let inFlight: Promise<void> = Promise.resolve();
  let writing = false;
  let warned = false;
  /** Set by the first drag/dblclick, so a slow `store.get` cannot overwrite a live gesture. */
  let userChanged = false;

  function warnOnce(error: unknown): void {
    if (warned) return;
    warned = true;
    console.warn('[intch-motion-kit] layout persistence unavailable', error);
  }

  /**
   * The window-independent half: each track inside its own limits.
   *
   * This is the ONLY clamp `desired` goes through, because these bounds are properties of the
   * layout itself, not of the window that happens to be open.
   */
  function clampLimits(next: LayoutSizes): LayoutSizes {
    return {
      left: clampNumber(next.left, LAYOUT_LIMITS.left.min, LAYOUT_LIMITS.left.max),
      right: clampNumber(next.right, LAYOUT_LIMITS.right.min, LAYOUT_LIMITS.right.max),
      timeline: clampNumber(next.timeline, LAYOUT_LIMITS.timeline.min, Number.POSITIVE_INFINITY),
    };
  }

  /**
   * The window-dependent half: squeeze an already-limit-clamped set into the shell that exists.
   *
   * The sum rule is the one that matters: `left` and `right` are each legal on their own at their
   * maxima, but together they can leave the viewport a sliver. When the shell has no measurable
   * width (jsdom, or a shell that is `display:none`) the rule is skipped rather than applied
   * against a zero — clamping to a lie is worse than not clamping.
   *
   * Nothing here is ever written back to `desired`: that is what makes a window resize reversible.
   */
  function clampToWindow(next: LayoutSizes): LayoutSizes {
    const gap = readGap(shell);
    let left = next.left;
    let right = next.right;

    const width = shell.clientWidth;
    if (width > 0) {
      // clientWidth = content + padding, so the three tracks share `width - 4 gaps`
      // (two of padding, two between the tracks).
      const budget = width - LAYOUT_LIMITS.viewportMin - gap * 4;
      let over = left + right - budget;
      if (over > 0) {
        // Take it off the larger column first: shrinking the one the user already made small is
        // how a "resize the window" ends up destroying the layout they set up.
        const order: Array<'left' | 'right'> = left >= right ? ['left', 'right'] : ['right', 'left'];
        for (const key of order) {
          if (over <= 0) break;
          const current = key === 'left' ? left : right;
          const min = LAYOUT_LIMITS[key].min;
          const take = Math.min(over, current - min);
          if (take <= 0) continue;
          if (key === 'left') left -= take;
          else right -= take;
          over -= take;
        }
      }
    }

    const height = shell.clientHeight;
    const timelineMax =
      height > 0
        ? Math.max(LAYOUT_LIMITS.timeline.min, height * LAYOUT_LIMITS.timeline.maxFraction)
        : Number.POSITIVE_INFINITY;
    const timeline = clampNumber(next.timeline, LAYOUT_LIMITS.timeline.min, timelineMax);

    return { left, right, timeline };
  }

  function applyVars(): void {
    shell.style.setProperty('--left-w', `${applied.left}px`);
    shell.style.setProperty('--right-w', `${applied.right}px`);
    shell.style.setProperty('--tl-h', `${applied.timeline}px`);
  }

  /** DESIRED is what goes to disk — see the module comment. */
  function writeNow(): Promise<void> {
    if (!store) return Promise.resolve();
    const snapshot: LayoutSizes = { ...desired };
    writing = true;
    inFlight = store.set(LAYOUT_SETTINGS_KEY, snapshot).catch(warnOnce).finally(() => {
      writing = false;
    });
    return inFlight;
  }

  function schedulePersist(): void {
    if (!store || disposed) return;
    if (persistTimer !== null) clearTimeout(persistTimer);
    persistTimer = setTimeout(() => {
      persistTimer = null;
      void writeNow();
    }, PERSIST_DEBOUNCE_MS);
  }

  /** Recompute what fits and write it to CSS. Never touches `desired`, never persists. */
  function reflow(): void {
    const next = clampToWindow(desired);
    applied.left = next.left;
    applied.right = next.right;
    applied.timeline = next.timeline;
    applyVars();
  }

  /**
   * A user action: it redefines `desired`, so it is the only path that persists.
   *
   * The tracks NOT named in `partial` fall back to `desired`, never to `applied` — otherwise
   * resizing one column would quietly demote the other from "what I asked for" to "what happened
   * to fit at the time", which is the same data loss from a different direction. A caller that
   * means "the value I can see" (a drag) passes it in explicitly; the drag start is `applied`.
   */
  function request(partial: Partial<LayoutSizes>, persist: boolean): void {
    if (disposed) return;
    const next = clampLimits({
      left: partial.left ?? desired.left,
      right: partial.right ?? desired.right,
      timeline: partial.timeline ?? desired.timeline,
    });
    const changed =
      next.left !== desired.left ||
      next.right !== desired.right ||
      next.timeline !== desired.timeline;
    desired.left = next.left;
    desired.right = next.right;
    desired.timeline = next.timeline;
    reflow();
    if (persist && changed) schedulePersist();
  }

  // --- handles ------------------------------------------------------------

  function makeHandle(key: SplitKey): HTMLDivElement {
    const node = document.createElement('div');
    node.className = 'splitter';
    node.dataset.split = key;
    node.title =
      key === 'timeline'
        ? 'Drag to resize the timeline (double-click to reset)'
        : 'Drag to resize the column (double-click to reset)';
    shell.appendChild(node);
    return node;
  }

  const handles: Record<SplitKey, HTMLDivElement> = {
    left: makeHandle('left'),
    right: makeHandle('right'),
    timeline: makeHandle('timeline'),
  };

  interface Drag {
    key: SplitKey;
    pointerId: number;
    startX: number;
    startY: number;
    start: LayoutSizes;
  }
  let drag: Drag | null = null;

  function endDrag(): void {
    if (!drag) return;
    handles[drag.key].classList.remove('dragging');
    shell.classList.remove('resizing');
    drag = null;
  }

  for (const key of ['left', 'right', 'timeline'] as const) {
    const node = handles[key];

    node.addEventListener('pointerdown', (event) => {
      if (event.button !== 0) return;
      event.preventDefault();
      userChanged = true;
      drag = {
        key,
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        // APPLIED, not desired: the handle the user grabbed is where the layout actually is, so
        // that is what `+ dx` has to be measured from.
        start: { ...applied },
      };
      capture(node, event.pointerId, true);
      node.classList.add('dragging');
      shell.classList.add('resizing');
    });

    node.addEventListener('pointermove', (event) => {
      if (!drag || drag.pointerId !== event.pointerId) return;
      const dx = event.clientX - drag.startX;
      const dy = event.clientY - drag.startY;
      // The right column and the timeline grow towards the pointer's *origin*, so their deltas
      // are negated: dragging the right splitter left widens the right column.
      if (key === 'left') request({ left: drag.start.left + dx }, true);
      else if (key === 'right') request({ right: drag.start.right - dx }, true);
      else request({ timeline: drag.start.timeline - dy }, true);
    });

    const release = (event: PointerEvent): void => {
      if (!drag || drag.pointerId !== event.pointerId) return;
      capture(node, event.pointerId, false);
      endDrag();
    };
    node.addEventListener('pointerup', release);
    node.addEventListener('pointercancel', release);

    node.addEventListener('dblclick', () => {
      userChanged = true;
      request({ [key]: LAYOUT_DEFAULTS[key] } as Partial<LayoutSizes>, true);
    });
  }

  // --- window ---------------------------------------------------------------

  const onWindowResize = (): void => {
    // Reflow only. `desired` is untouched and nothing is persisted: a narrower window may not be
    // able to honour the user's widths without closing the viewport, but that is a fact about the
    // window, not a decision the user made — widen it again and their layout comes back.
    reflow();
  };
  win?.addEventListener('resize', onWindowResize);

  // --- start ----------------------------------------------------------------

  reflow();

  if (store) {
    store.get<unknown>(LAYOUT_SETTINGS_KEY, null).then((value) => {
      if (disposed || userChanged) return;
      const restored = readStored(value);
      // The stored value IS the desired one, so it goes in through the same path a drag uses —
      // minus the persist, because writing back what was just read is noise.
      if (restored) request(restored, false);
    }, warnOnce);
  }

  return {
    sizes() {
      return { ...applied };
    },
    desired() {
      return { ...desired };
    },
    pending() {
      return persistTimer !== null || writing;
    },
    set(partial) {
      userChanged = true;
      request(partial, true);
    },
    setTimelineVisible(visible) {
      handles.timeline.hidden = !visible;
    },
    async flush() {
      if (persistTimer !== null) {
        clearTimeout(persistTimer);
        persistTimer = null;
        await writeNow();
      }
      await inFlight;
    },
    dispose() {
      if (disposed) return;
      disposed = true;
      endDrag();
      // A pending write is the user's last drag: send it rather than dropping it. Nothing awaits
      // this — `flush()` is the awaited path.
      if (persistTimer !== null) {
        clearTimeout(persistTimer);
        persistTimer = null;
        void writeNow();
      }
      win?.removeEventListener('resize', onWindowResize);
      for (const node of Object.values(handles)) node.remove();
      shell.classList.remove('resizing');
      shell.style.removeProperty('--left-w');
      shell.style.removeProperty('--right-w');
      shell.style.removeProperty('--tl-h');
    },
  };
}
